// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { screen } from "@monsma/XDL/Helpers";
import Folder from "@monsma/XDL/Icons/Folder";
import StickyNote from "@monsma/XDL/Icons/StickyNote";
import { removeLinkStyle } from "@monsma/XDL/Utilities/sharedStyles";
import React, { Children, FC, PropsWithChildren } from "react";
import { css } from "styled-components";

export interface LineStyles {
    productImage?: ProductImageStyles;

    productPartNumbers?: ProductPartNumbersStyles;
    productDescription?: ProductDescriptionStyles;
    productBrand?: ProductBrandStyles;
    productAvailability?: ProductAvailabilityStyles;

    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: TypographyProps;

    addToListLink?: LinkPresentationProps;
    addToListLink1?: LinkPresentationProps;
    removeLink?: LinkPresentationProps;
    cartLineNotes?: CartLineNotesStyles;

    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
}

export const lineStyle: LineStyles = {
    productImage: {
        image: {
            errorTypographyProps: {
                css: css`
                    font-size: 11px;
                `,
            },
            css: css`
                width: 100%;

                img {
                    height: 100%;
                    object-fit: contain;
                    object-position: center;

                    @media print {
                        width: auto;
                        height: auto;
                        max-width: 100%;
                        max-height: 100%;
                        object-fit: unset;
                        object-position: unset;
                    }
                }

                @media print {
                    display: block;
                    text-align: center;
                }
            `,
        },
        linkWrappingImage: {
            css: css`
                background: ${getColor("common.background")};
                border: 1px solid ${getColor("common.ui12")};
                border-radius: 4px;
                overflow: hidden;
                width: 110px;
                height: 110px;
                padding: 1px;
                justify-content: center;

                ${screen("lte", 460)} {
                    width: 100%;
                    height: 190px;
                }

                & img {
                    width: auto;
                    height: auto;
                    max-height: 100%;
                    max-width: 100%;
                }
            `,
        },
    },

    productPartNumbers: {
        container: {
            gap: 5,
            css: css`
                font-size: 13px;
            `,
        },
    },

    productDescription: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                weight: "bold",
                size: 14,
                css: css`
                    width: 100%;
                    ${wrapInContainerStyles};
                    line-height: 19px;
                `,
            },
        },
    },

    productBrand: {
        nameText: {
            css: css`
                display: none;
            `,
        },
        nameLink: {
            css: css`
                display: none;
            `,
        },
    },

    productAvailability: {
        container: {
            gap: 0,
            css: css`
                flex-grow: 0;
                margin-top: 20px;
            `,
        },
    },

    productPrice: {
        price: {
            priceText: {
                size: 16,
                weight: "bold",
                color: "common.ui10",
            },
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                    font-size: 13px;
                `,
            },
        },
        quoteMessage: {
            text: {
                css: css`
                    margin-right: 5px;
                `,
            },
        },
    },

    extendedUnitNetPriceText: {
        size: 13,
        weight: "bold",
    },

    addToListLink: {
        icon: {
            iconProps: {
                src: Folder,
                size: 12,
                color: "common.ui5",
            },
            position: "left",
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")};
            position: absolute;
            bottom: 0;
            right: 0;
            ${screen(768, 1088)} {
                right: auto;
                left: 0;
            }

            ${screen("lte", 590)} {
                display: none;
            }
        `,
    },

    addToListLink1: {
        css: css`
            display: none;

            ${screen("lte", 590)} {
                display: inline-flex;
                bottom: 45px;
            }
        `,
    },

    removeLink: {
        ...removeLinkStyle,
        css: css`
            ${removeLinkStyle?.css}
            position: absolute;
            bottom: 0;
            left: 0;
            ${screen("lte", 590)} {
                left: auto;
                right: 0;
            }
        `,
    },

    cartLineNotes: {
        wrapper: {
            css: css`
                label {
                    font-size: 12px;
                    font-weight: bold;
                    margin: 10px 0;
                    display: block;
                }
            `,
        },
        toggleLink: {
            icon: {
                iconProps: {
                    src: StickyNote,
                    size: 12,
                    color: "common.ui5",
                },
                position: "left",
            },
            css: css`
                ${getProp("theme.link.defaultProps.css")};
                position: absolute;
                top: -20px;
                left: 0;
                ${screen(768, 1088)} {
                    position: static;
                }

                ${screen("lte", 590)} {
                    top: auto;
                    bottom: 50px;
                }
            `,
        },
    },

    productInfoSubtotalHeadingAndText: {
        text: {
            size: 16,
            lineHeight: "20px",
            weight: "bold",
            color: "common.ui10",
        },
        wrapper: {
            css: css`
                margin-top: 10px;
                @media print {
                    margin-top: 0;
                    margin-left: 10px;
                }
            `,
        },
    },
};

export const lineContainerStyles = {
    container: {
        css: css`
            display: grid;
            grid-template-columns: 110px 2fr 1fr 1fr;
            grid-column-gap: 20px;
            grid-row-gap: 5px;

            background: ${getColor("common.background")};
            border: 1px solid ${getColor("common.ui12")};
            border-top: 0;
            padding: 20px;
            width: 100%;

            ${screen(768, 1024)} {
                grid-template-columns: 110px 2fr 1fr 1.5fr;
            }
            ${screen("lte", 460)} {
                grid-template-columns: 1fr 1.5fr;
            }

            /* debugging only
             & > div {
                 outline: 1px solid black;
             }*/

            @media print {
                -webkit-column-break-inside: avoid;
                page-break-inside: avoid;
                break-inside: avoid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                float: left;
                border-width: 2px;
                padding: 5px 20px;
                grid-row-gap: 0;
            }
        `,
        // The float change on print for the PDF - the grid/flex not working on PDF.
    },
    image: {
        css: css`
            grid-column: 1 / 2;
            grid-row: 1 / 4;
            ${screen("lte", 590)} {
                grid-row: 1 / 2;
            }
            ${screen("lte", 460)} {
                grid-column: 1 / 3;
            }
            @media print {
                display: none;
            }
        `,
    },
    info: {
        css: css`
            grid-column: 2 / 3;
            grid-row: 1 / 3;

            position: relative;
            padding-bottom: 30px;

            ${screen(768, 1024)} {
                grid-column: 2 / 4;
                grid-row: 1 / 3;
            }
            ${screen(590, 666)} {
                grid-column: 2 / 4;
                grid-row: 1 / 3;
            }
            ${screen("lte", 590)} {
                grid-column: 2 / 5;
                grid-row: 1 / 3;

                @media print {
                    float: right;
                }
            }
            ${screen("lte", 460)} {
                grid-column: 1 / 3;
                grid-row: 2 / 3;
                padding-bottom: 0;
            }
            @media print {
                padding-bottom: 5px;
                grid-column: 1/ 5;
            }
        `,
    },
    qty: {
        css: css`
            grid-column: 3 / 4;
            grid-row: 1 / 3;
            ${screen(768, 1024)} {
                grid-column: 4 / 5;
                grid-row: 1 / 2;
            }
            ${screen(590, 666)} {
                grid-column: 4 / 5;
                grid-row: 1 / 2;
            }
            ${screen("lte", 590)} {
                grid-column: 2 / 3;
                grid-row: 2 / 3;
                padding-bottom: 30px;
            }
            ${screen("lte", 460)} {
                grid-column: 1 / 2;
                grid-row: 3 / 4;
            }
            @media print {
                float: left;
                display: flex;
                grid-row: 3 / 3;
                padding-bottom: 0;
                grid-column: 1 / span 2;
            }
        `,
    },
    price: {
        css: css`
            grid-column: 4 / 5;
            grid-row: 1 / 3;

            position: relative;
            padding-bottom: 30px;

            ${screen(768, 1024)} {
                grid-column: 4 / 5;
                grid-row: 2 / 3;
            }
            ${screen(590, 666)} {
                grid-column: 4 / 5;
                grid-row: 2 / 3;
            }
            ${screen("lte", 590)} {
                grid-column: 3 / 5;
                grid-row: 2 / 3;
                padding-bottom: 85px;
                text-align: right;
            }
            ${screen("lte", 460)} {
                grid-column: 2 / 3;
                grid-row: 3 / 4;
            }
            @media print {
                float: right;
                display: flex;
                justify-content: space-between;
                padding: 0;
                grid-row: 3 / 3;
                grid-column: 3 / span 2;
            }
        `,
    },
    note: {
        css: css`
            grid-column: 2 / 5;
            grid-row: 3 / 4;
            position: relative;
            ${screen("lte", 460)} {
                grid-column: 1 / 3;
                grid-row: 4 / 5;
            }
        `,
    },
};

interface OwnProps {
    Image: FC<PropsWithChildren>;
    Info: FC<PropsWithChildren>;
    Qty: FC<PropsWithChildren>;
    Price: FC<PropsWithChildren>;
    Note: FC<PropsWithChildren>;
}

const Image: FC = () => null;
const Info: FC = () => null;
const Qty: FC = () => null;
const Price: FC = () => null;
const Note: FC = () => null;

const LineContainer: React.FC<PropsWithChildren> & OwnProps = ({ children }) => {
    const childrenArray = Children.toArray(children) as React.ReactElement<PropsWithChildren>[];
    const imageSlot = childrenArray.find(child => child.type === Image);
    const infoSlot = childrenArray.find(child => child.type === Info);
    const qtySlot = childrenArray.find(child => child.type === Qty);
    const priceSlot = childrenArray.find(child => child.type === Price);
    const noteSlot = childrenArray.find(child => child.type === Note);

    return (
        <StyledWrapper {...lineContainerStyles.container}>
            <StyledWrapper {...lineContainerStyles.image}>{imageSlot?.props?.children}</StyledWrapper>
            <StyledWrapper {...lineContainerStyles.info}>{infoSlot?.props?.children}</StyledWrapper>
            <StyledWrapper {...lineContainerStyles.qty}>{qtySlot?.props?.children}</StyledWrapper>
            <StyledWrapper {...lineContainerStyles.price}>{priceSlot?.props?.children}</StyledWrapper>
            <StyledWrapper {...lineContainerStyles.note}>{noteSlot?.props?.children}</StyledWrapper>
        </StyledWrapper>
    );
};

LineContainer.Image = Image;
LineContainer.Info = Info;
LineContainer.Qty = Qty;
LineContainer.Price = Price;
LineContainer.Note = Note;

export const lineHeaderStyles = {
    wrapper: {
        css: css`
            background: #0b2e49;
            display: flex;
            border-radius: 4px 4px 0 0;
            color: white;
            font-size: 13px;
            font-weight: bold;

            ${screen("lte", 590)} {
                display: none;
            }
        `,
    },
    number: {
        css: css`
            width: calc(110px + 50%);
            padding: 10px 15px;

            ${screen(768, 1024)} {
                width: calc(110px + 70%);
            }

            ${screen(590, 666)} {
                width: calc(110px + 70%);
            }
        `,
    },
    qty: {
        css: css`
            width: 25%;
            padding: 10px 15px;

            ${screen(768, 1024)} {
                width: 10%;
                position: relative;

                &:after {
                    content: "/";
                    position: absolute;
                    top: 10px;
                    right: -3px;
                }
            }

            ${screen(590, 666)} {
                width: 10%;
                position: relative;

                &:after {
                    content: "/";
                    position: absolute;
                    top: 10px;
                    right: -3px;
                }
            }
        `,
    },
    total: {
        css: css`
            width: 25%;
            padding: 10px 15px;

            ${screen(768, 1024)} {
                width: 20%;
            }

            ${screen(590, 666)} {
                width: 25%;
            }
        `,
    },
};

export const LineHeader = () => {
    return (
        <StyledWrapper {...lineHeaderStyles.wrapper}>
            <StyledWrapper {...lineHeaderStyles.number}>{translate("Number")}</StyledWrapper>
            <StyledWrapper {...lineHeaderStyles.qty}>{translate("Qty")}</StyledWrapper>
            <StyledWrapper {...lineHeaderStyles.total}>{translate("Total")}</StyledWrapper>
        </StyledWrapper>
    );
};

export default LineContainer;
